"use client";

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { trackEvent } from "@/lib/analytics";
import { searchHubIndex } from "@/lib/conditions/hub/search-match";
import type { HubSearchIndex, HubSearchResult, HubSport, HubSportKey } from "@/lib/conditions/hub/types";
import { HubCloseIcon, HubIcon } from "./hub-icons";
import motion from "./hub-motion.module.css";
import { HUB_BOARD_ID, HUB_SPORT_PARAM, selectHubSport } from "./hub-sport-select";

/**
 * The hub search combobox (F4). The static index (`/api/conditions/search-index`, catalogue only,
 * no scores) is fetched on first focus. Results are grouped Spots, Resorts, Areas, Sports, at most
 * eight. Arrow keys move, Enter opens, Escape closes then clears, a pointer down outside closes.
 * A sport result selects that sport on the board below instead of navigating.
 */

export const HUB_SEARCH_INDEX_URL = "/api/conditions/search-index";

export type HubSearchProps = Readonly<{
  /** Shown sports, picker order (named paddle profiles only when public). */
  sports: readonly HubSport[];
  /** Snow sports with nothing in season: their sport results read "Opens Dec" / "Off-season". */
  offSeasonSports?: readonly HubSportKey[];
  /** Override the index URL (tests). */
  indexUrl?: string;
}>;

type LoadState = "idle" | "loading" | "ready" | "error";

const DESKTOP_QUERY = "(min-width: 768px)";

function subscribeDesktop(callback: () => void): () => void {
  const query = window.matchMedia(DESKTOP_QUERY);
  query.addEventListener("change", callback);
  return () => query.removeEventListener("change", callback);
}

/** True at md and up once hydrated; false on the server (the phone placeholder). */
function useDesktop(): boolean {
  return useSyncExternalStore(subscribeDesktop, () => window.matchMedia(DESKTOP_QUERY).matches, () => false);
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function Highlighted({ name, match }: Readonly<{ name: string; match: HubSearchResult["match"] }>) {
  if (!match || match[1] <= match[0]) return <>{name}</>;
  return (
    <>
      {name.slice(0, match[0])}
      <mark className="rounded-[3px] bg-[#DDEFF1] font-bold text-navy md:bg-transparent">{name.slice(match[0], match[1])}</mark>
      {name.slice(match[1])}
    </>
  );
}

function resultHref(result: HubSearchResult): string {
  if (result.href) return result.href;
  return result.sportKey ? `/conditions?${HUB_SPORT_PARAM}=${encodeURIComponent(result.sportKey)}#${HUB_BOARD_ID}` : "/conditions";
}

export function HubSearch({ sports, offSeasonSports = [], indexUrl = HUB_SEARCH_INDEX_URL }: HubSearchProps) {
  const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const inputId = `hub-search-${baseId}`;
  const listId = `${inputId}-results`;
  const optionId = (position: number) => `${inputId}-opt-${position}`;

  const desktop = useDesktop();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [index, setIndex] = useState<HubSearchIndex | null>(null);
  const [load, setLoad] = useState<LoadState>("idle");
  const requested = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const ensureIndex = useCallback(() => {
    if (requested.current) return;
    requested.current = true;
    setLoad("loading");
    fetch(indexUrl)
      .then((response) => (response.ok ? (response.json() as Promise<HubSearchIndex>) : Promise.reject(new Error(String(response.status)))))
      .then((data) => {
        setIndex(data);
        setLoad("ready");
      })
      .catch(() => {
        requested.current = false;
        setLoad("error");
      });
  }, [indexUrl]);

  const trimmed = query.trim();
  const outcome = useMemo(
    () => (index && trimmed ? searchHubIndex(index, trimmed, sports) : null),
    [index, trimmed, sports],
  );
  const flat = useMemo(() => (outcome ? outcome.groups.flatMap((group) => group.results) : []), [outcome]);
  const offSet = useMemo(() => new Set(offSeasonSports), [offSeasonSports]);
  const panelOpen = open && trimmed.length > 0;
  const activeIndex = active >= 0 && active < flat.length ? active : -1;

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setActive(-1);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [panelOpen]);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const track = (result: HubSearchResult, position: number) => {
    trackEvent("conditions_hub_search", { query_length: trimmed.length, result_type: result.kind, position: position + 1 });
  };

  const chooseSport = (result: HubSearchResult, position: number) => {
    if (!result.sportKey) return;
    track(result, position);
    selectHubSport(result.sportKey, "search");
    close();
    const board = document.getElementById(HUB_BOARD_ID);
    board?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  };

  const onOptionClick = (event: MouseEvent<HTMLAnchorElement>, result: HubSearchResult, position: number) => {
    if (!result.href && result.sportKey) {
      event.preventDefault();
      chooseSport(result, position);
      return;
    }
    track(result, position);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (flat.length === 0) return;
      event.preventDefault();
      setOpen(true);
      if (event.key === "ArrowDown") setActive(activeIndex < 0 ? 0 : (activeIndex + 1) % flat.length);
      else setActive(activeIndex <= 0 ? flat.length - 1 : activeIndex - 1);
      return;
    }
    if (event.key === "Enter") {
      if (!panelOpen || flat.length === 0) return;
      event.preventDefault();
      const position = activeIndex >= 0 ? activeIndex : 0;
      document.getElementById(optionId(position))?.click();
      return;
    }
    if (event.key === "Escape") {
      if (panelOpen) {
        event.preventDefault();
        close();
      } else if (query) {
        event.preventDefault();
        setQuery("");
      }
    }
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !containerRef.current?.contains(next)) close();
  };

  let live = "";
  if (trimmed) {
    if (load === "loading" || (load === "idle" && !index)) live = "Loading search";
    else if (load === "error") live = "Search is unavailable right now";
    else if (outcome) live = outcome.shown === 0 ? "No match" : `${outcome.shown} ${outcome.shown === 1 ? "result" : "results"}`;
  }

  const countText = outcome && outcome.shown > 0
    ? `Showing ${outcome.shown}${outcome.total > outcome.shown ? ` of ${outcome.total}` : ""} ${outcome.total === 1 ? "match" : "matches"}`
    : "";

  const groupOffsets: number[] = [];
  if (outcome) {
    let offset = 0;
    for (const group of outcome.groups) {
      groupOffsets.push(offset);
      offset += group.results.length;
    }
  }

  return (
    <div ref={containerRef} onBlur={onBlur} className="relative z-[7] mt-[18px] md:mt-[26px] md:max-w-[700px]" data-hub-search="">
      <label htmlFor={inputId} className="sr-only md:not-sr-only md:mb-2 md:block md:text-[14px] md:font-semibold md:text-[#D5DCE6]">
        Search a spot, resort or area
      </label>
      <div className="relative flex h-[52px] items-center gap-2.5 rounded-[14px] bg-white pl-3.5 pr-1 shadow-[0_14px_30px_rgba(0,0,0,0.22)] focus-within:shadow-[0_0_0_3px_rgba(91,192,206,0.55),0_14px_30px_rgba(0,0,0,0.22)] md:h-14 md:pl-[18px] md:pr-1.5 md:shadow-[0_18px_40px_rgba(6,12,24,0.35)] md:focus-within:shadow-[0_0_0_3px_#5BC0CE,0_18px_40px_rgba(6,12,24,0.35)]">
        <span aria-hidden="true" className="flex shrink-0 text-teal-dark md:text-[#566072] [&_svg]:size-5 md:[&_svg]:size-[22px]">
          <HubIcon name="search" size={22} strokeWidth={2} />
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="search"
          enterKeyHint="go"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={panelOpen}
          aria-activedescendant={panelOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={desktop ? "Try Hossegor, Chamonix or wing foil" : "Search a spot, resort or area"}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActive(-1);
            ensureIndex();
          }}
          onFocus={() => {
            ensureIndex();
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className="h-12 min-w-0 flex-auto border-0 bg-transparent p-0 font-[inherit] text-[16px] font-medium text-navy outline-none placeholder:text-[#566072] md:text-[17px] md:placeholder:text-text-muted"
        />
        {query ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              setActive(-1);
              inputRef.current?.focus();
            }}
            className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-0 bg-transparent text-[#566072]"
          >
            <HubCloseIcon size={desktop ? 16 : 14} />
          </button>
        ) : null}
      </div>
      <p className="sr-only" aria-live="polite">{live}</p>
      <div
        className={`${motion.drop} absolute inset-x-0 top-[58px] z-10 max-h-[340px] overflow-y-auto overscroll-contain rounded-[14px] border border-border bg-white p-1.5 text-text-body shadow-[0_22px_48px_rgba(22,35,59,0.28)] md:top-16 md:max-h-none md:overflow-visible md:rounded-[16px] md:p-2 md:shadow-[0_28px_64px_rgba(6,12,24,0.38)] ${
          panelOpen ? "block" : "hidden"
        }`}
        data-hub-search-panel=""
      >
        {panelOpen && !outcome && load !== "error" ? (
          <p className="m-0 px-2.5 py-3.5 text-[14px] text-[#566072] md:text-[15px]">Loading search</p>
        ) : null}
        {panelOpen && load === "error" ? (
          <p className="m-0 px-2.5 py-3.5 text-[14px] text-[#566072] md:text-[15px]">Search is unavailable right now. Try again in a moment.</p>
        ) : null}
        {panelOpen && outcome && outcome.shown === 0 ? (
          <p className="m-0 px-2.5 py-3.5 text-[14px] leading-[1.45] text-[#566072] md:px-3 md:text-[15px] md:text-text-body">
            No match. Try a resort, beach or area name.
          </p>
        ) : null}
        <div id={listId} role="listbox" aria-label="Search results">
          {panelOpen && outcome
            ? outcome.groups.map((group, groupIndex) => (
                <div key={group.kind} role="group" aria-labelledby={`${listId}-${group.kind}`} className="pb-1">
                  <p
                    id={`${listId}-${group.kind}`}
                    className={`m-0 px-2.5 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.1em] text-teal-dark md:text-[11.5px] md:text-[#566072] ${
                      groupIndex > 0 ? "mt-0.5 border-t border-[#EDF1EC] md:mt-0 md:border-t-0" : ""
                    }`}
                  >
                    {group.label}
                  </p>
                  {group.results.map((result, resultIndex) => {
                    const at = (groupOffsets[groupIndex] ?? 0) + resultIndex;
                    const selected = at === activeIndex;
                    const area = result.kind === "area";
                    const offSeason = result.kind === "sport" && result.sportKey !== null && offSet.has(result.sportKey);
                    return (
                      <Link
                        key={`${result.kind}:${result.id}`}
                        id={optionId(at)}
                        role="option"
                        aria-selected={selected}
                        tabIndex={-1}
                        href={resultHref(result)}
                        prefetch={false}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActive(at)}
                        onClick={(event) => onOptionClick(event, result, at)}
                        data-hub-search-option={result.kind}
                        className={`flex min-h-12 items-center gap-2.5 rounded-[10px] px-2 py-1.5 text-text-body no-underline hover:text-text-body md:min-h-11 md:gap-3 md:px-2.5 md:py-0 ${
                          selected ? "bg-[#E4F2F3] md:shadow-[inset_3px_0_0_#16808F]" : "bg-transparent hover:bg-[#F4F8F7]"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={`flex size-8 shrink-0 items-center justify-center rounded-[9px] ${
                            area ? "bg-[#EEF1EA] text-[#566072] md:bg-[#E4F2F3] md:text-teal-dark" : "bg-[#E4F2F3] text-teal-dark"
                          }`}
                        >
                          <HubIcon name={result.icon} size={18} strokeWidth={1.8} />
                        </span>
                        <span className="flex min-w-0 flex-auto flex-col gap-px">
                          <span className="truncate text-[14.5px] font-semibold leading-[1.2] text-navy md:text-[15px] md:font-bold md:leading-[18px]">
                            <Highlighted name={result.name} match={result.match} />
                          </span>
                          <span className="truncate text-[12px] leading-[1.3] text-[#566072] md:hidden">{result.secondaryPhone}</span>
                          <span className="hidden truncate text-[12.5px] leading-4 text-[#566072] md:block">{result.secondary}</span>
                        </span>
                        {offSeason ? (
                          <span className="shrink-0 whitespace-nowrap text-[12px] font-semibold text-[#566072] md:text-[12.5px] md:text-text-muted">
                            <span className="md:hidden">Off-season</span>
                            <span className="hidden md:inline">Opens Dec</span>
                          </span>
                        ) : null}
                      </Link>
                    );
                  })}
                </div>
              ))
            : null}
        </div>
        {panelOpen && outcome && outcome.shown > 0 ? (
          <p className="m-0 mt-1 hidden justify-between gap-3 border-t border-[#EDF1EC] px-2.5 pb-0.5 pt-2 text-[12px] text-text-muted md:flex">
            <span>{countText}</span>
            <span aria-hidden="true">Arrow keys to move, Enter to open, Esc to close</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}
